import React, { useCallback, useState } from 'react';
import {
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { EditPencilIcon } from '../../components/EditPencilIcon';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import {
  deactivatePermissionConfiguration,
  getPermissionConfigurations,
  getPermissionOrgPolicy,
} from '../../services/hrPolicyConfigurationService';
import type {
  PermissionConfiguration,
  PermissionOrgPolicy,
} from '../../types/hrPolicyConfiguration';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { formatMinutes } from '../../utils/dateTime';
import { normalizeApiError } from '../../api/apiError';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'PermissionConfigurationList'>;

function PolicyValue({
  label,
  value,
  palette,
}: {
  label: string;
  value: string;
  palette: { textPrimary: string; textSecondary: string };
}) {
  return (
    <View style={s.valueRow}>
      <Text style={[s.text, { color: palette.textSecondary }]}>{label}</Text>
      <Text style={[s.value, { color: palette.textPrimary }]}>{value}</Text>
    </View>
  );
}

export function PermissionConfigurationListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const canUpdate = auth.hasPermission(PERMISSIONS.SETTINGS_UPDATE);
  const [policy, setPolicy] = useState<PermissionOrgPolicy>({
    permissionConfigurationId: null,
    organizationId: null,
    maximumPermissionsPerMonth: 0,
    minimumDurationMinutes: 0,
    maximumDurationMinutes: 0,
    notes: '',
    rowVersion: 1,
  });
  const [items, setItems] = useState<PermissionConfiguration[]>([]);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(
    async (refresh = false) => {
      setRefreshing(refresh);
      try {
        const [savedPolicy, page] = await Promise.all([
          getPermissionOrgPolicy(),
          getPermissionConfigurations(search),
        ]);
        setPolicy(savedPolicy);
        setItems(page.items.filter(item => isActiveConfigurationRecord(item.isActive)));
        setError(null);
      } catch (caught) {
        setError(normalizeApiError(caught).message);
      } finally {
        setRefreshing(false);
      }
    },
    [search],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const listHeader = (
    <View style={s.header}>
      <AppCard style={s.card}>
        <View style={s.titleRow}>
          <Text style={[s.title, { color: palette.textPrimary, flex: 1 }]}>
            Overall permission policy
          </Text>
          {canUpdate ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Edit"
              hitSlop={8}
              onPress={() =>
                navigation.navigate(routes.permissionConfigurationPolicy)
              }
              style={s.editHit}
            >
              <EditPencilIcon color={palette.primary} size={22} />
            </Pressable>
          ) : null}
        </View>
        <Text style={[s.text, { color: palette.textSecondary }]}>
          The monthly limit is shared by the employee across every permission type.
        </Text>
        <PolicyValue
          label="Maximum permissions per month"
          value={
            policy.maximumPermissionsPerMonth > 0
              ? String(policy.maximumPermissionsPerMonth)
              : 'Not configured'
          }
          palette={palette}
        />
        <PolicyValue
          label="Minimum duration per permission"
          value={
            policy.minimumDurationMinutes > 0
              ? formatMinutes(policy.minimumDurationMinutes)
              : 'Not configured'
          }
          palette={palette}
        />
        <PolicyValue
          label="Maximum duration per permission"
          value={
            policy.maximumDurationMinutes > 0
              ? formatMinutes(policy.maximumDurationMinutes)
              : 'Not configured'
          }
          palette={palette}
        />
        <View style={s.valueRow}>
          <Text style={[s.text, { color: palette.textSecondary }]}>Notes</Text>
          <Text style={[s.value, { color: palette.textPrimary }]}>
            {(policy.notes || '').trim() ? policy.notes : 'Not configured'}
          </Text>
        </View>
      </AppCard>
      <AppInput
        label="Search"
        value={search}
        onChangeText={setSearch}
        placeholder="Permission type name"
      />
      <AppButton title="Search" variant="secondary" onPress={() => load()} />
      {canUpdate ? (
        <AppButton
          title="Create permission type"
          onPress={() => navigation.navigate(routes.permissionConfigurationCreate)}
        />
      ) : null}
      {error ? (
        <Text accessibilityRole="alert" style={[s.error, { color: palette.error }]}>
          {error}
        </Text>
      ) : null}
    </View>
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.SETTINGS_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen keyboardSafe contentStyle={s.screen}>
        <ScreenHeader title="Permission configuration" onBack={navigation.goBack} />
        {error && items.length === 0 && !policy.permissionConfigurationId ? (
          <View style={s.body}>
            {listHeader}
            <ErrorState message={error} onRetry={() => load()} />
          </View>
        ) : (
          <FlatList
            style={s.listFrame}
            data={items}
            keyExtractor={item => String(item.permissionPolicyId)}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />
            }
            contentContainerStyle={s.list}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
            ListHeaderComponent={listHeader}
            ListEmptyComponent={
              <EmptyState
                title="No permission types"
                message="Save the overall policy, then create permission types such as Personal or Official."
              />
            }
            renderItem={({ item }) => (
              <AppCard style={s.card}>
                <View style={s.titleRow}>
                  <Text style={[s.title, { color: palette.textPrimary, flex: 1 }]}>
                    {formatDisplayLabel(item.permissionTypeName)}
                  </Text>
                  <ConfigurationDeleteButton
                    placement="inline"
                    entityLabel="permission type"
                    loading={softDelete.busy}
                    disabled={softDelete.busy}
                    onPress={() =>
                      softDelete.confirmAndDelete({
                        entityLabel: 'permission type',
                        recordName: formatDisplayLabel(item.permissionTypeName),
                        deleteRecord: async () => {
                          await deactivatePermissionConfiguration(item);
                        },
                        onSuccess: () => {
                          load(true).catch(() => undefined);
                        },
                        errorMessage: caught =>
                          normalizeApiError(caught).message ||
                          'Unable to delete permission type.',
                      })
                    }
                  />
                </View>
                <Text style={[s.text, { color: palette.textSecondary }]}>
                  {formatDisplayLabel(item.permissionTypeCode)}
                </Text>
                {item.description ? (
                  <Text style={[s.text, { color: palette.textSecondary }]}>
                    {item.description}
                  </Text>
                ) : null}
              </AppCard>
            )}
          />
        )}
      </AppScreen>
    </PermissionGuard>
  );
}

const s = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  header: { gap: spacing.sm, marginBottom: spacing.sm },
  listFrame: { flex: 1, marginTop: spacing.lg },
  list: { flexGrow: 1, gap: spacing.sm, paddingBottom: spacing.xxl },
  card: { gap: spacing.xs },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  title: typography.sectionTitle,
  text: typography.body,
  value: { ...typography.body, fontWeight: '700' },
  valueRow: { gap: 2 },
  error: typography.caption,
  editHit: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
